import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';

function HowIWork() {
    const phases = [
        {
            number: "01",
            title: "Discover",
            description: "Deep-dive research — stakeholder interviews, competitive audits, user surveys, and heuristic evaluations to uncover real problems worth solving.",
            deliverables: ["Research Report", "User Personas", "Journey Maps", "Opportunity Areas"]
        },
        {
            number: "02",
            title: "Define",
            description: "Synthesise insights into a crisp problem statement and success metrics. Align the team on what we're building, why, and how we'll measure it.",
            deliverables: ["Problem Statement", "Design Brief", "KPIs", "Roadmap"]
        },
        {
            number: "03",
            title: "Design",
            description: "Rapid ideation, wireframes, and high-fidelity Figma prototypes. Iterate fast, test often, and refine until the experience is undeniably right.",
            deliverables: ["Wireframes", "Prototypes", "Design System", "Specs"]
        },
        {
            number: "04",
            title: "Deliver",
            description: "Handoff that engineers love — annotated specs, developer-ready tokens, QA reviews, and post-launch monitoring to ensure quality ships.",
            deliverables: ["Dev Handoff", "Component Docs", "QA Review", "Analytics"]
        }
    ];

    return (
        <div className="bg-white py-12 md:py-28 px-4 md:px-20">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-12 md:mb-20">
                    <h2 className="text-sm font-semibold uppercase tracking-widest text-secondary mb-4">My Process</h2>
                    <h1 className="text-4xl md:text-6xl font-bold text-dj-dark mb-6">How I Work</h1>
                    <p className="text-lg text-gray-600">
                        Every engagement is different, but great design always starts with understanding — and ends with outcomes you can measure.
                    </p>
                </div>

                {/* Phases Grid - 2x2 on desktop, 1 column on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    {phases.map((phase) => (
                        <div key={phase.number} className="bg-dj-light rounded-lg p-8 md:p-10 hover:shadow-lg transition-shadow duration-300">
                            {/* Phase Number */}
                            <span className="text-6xl md:text-7xl font-bold text-primary opacity-20">
                                {phase.number}
                            </span>
                            
                            {/* Phase Title */}
                            <h3 className="text-2xl md:text-3xl font-bold text-dj-dark mt-2 mb-4">
                                {phase.title}
                            </h3>

                            {/* Description */}
                            <p className="text-gray-700 leading-relaxed mb-6">
                                {phase.description}
                            </p>

                            {/* Deliverables */}
                            <div className="pt-6 border-t border-gray-300">
                                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-4">Deliverables</p>
                                <ul className="space-y-3">
                                    {phase.deliverables.map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA Section */}
                <div className="mt-16 md:mt-24 bg-dj-dark rounded-lg p-8 md:p-12">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faCircle} className="text-green-500 text-xs" />
                                <p className="text-sm font-semibold text-green-500">Available for work</p>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-white">Taking on new projects in 2026</h2>
                        </div>
                        <div className="flex items-center justify-start md:justify-end flex-shrink-0">
                            <a href="#contact" className="bg-primary hover:bg-secondary text-dj-dark font-bold py-3 px-8 rounded transition-colors whitespace-nowrap">
                                Let's Connect
                            </a>
                        </div>
                    </div>
                    <p className="text-gray-400 mt-4 md:mt-0">Open to full-time roles, contract engagements, and advisory.</p>
                </div>
            </div>
        </div>
    );
}

export default HowIWork;
